import { create } from 'zustand';

import type { Call, CallOutcome } from '@/shared/services/api/types';

/**
 * Où en est l'appel, vu de cet appareil.
 *
 *  - `incoming`   : ça sonne ici ;
 *  - `outgoing`   : j'appelle, ça sonne chez l'autre ;
 *  - `connecting` : quelqu'un a décroché, la communication s'établit ;
 *  - `active`     : en communication ;
 *  - `ended`      : fini, l'écran le dit un instant avant de se refermer.
 */
export type CallPhase = 'incoming' | 'outgoing' | 'connecting' | 'active' | 'ended';

/**
 * Pourquoi l'appel s'est arrêté ici : l'issue du serveur, ou un fait propre à
 * cet appareil (un collègue, ou un autre de mes appareils, a décroché ;
 * la connexion a échoué ; les appels ne sont pas ouverts).
 */
export type CallEndReason = CallOutcome | 'ANSWERED_ELSEWHERE' | 'FAILED' | 'UNAVAILABLE';

/** Ce que la connexion à la pièce donne à voir : seul `poor` se dit à l'écran. */
export type CallQuality = 'good' | 'poor' | 'lost';

export interface CallSessionState {
  call: Call | null;
  phase: CallPhase | null;
  /** La pièce se reconnecte seule quand le réseau flanche : l'écran le dit. */
  reconnecting: boolean;
  quality: CallQuality | null;
  micOn: boolean;
  cameraOn: boolean;
  speakerOn: boolean;
  facing: 'user' | 'environment';
  /** Début de la communication, à l'horloge de ce téléphone : le chrono part de là. */
  connectedAt: number | null;
  endReason: CallEndReason | null;
  /** Message d'échec à montrer (micro refusé, réseau coupé au moment d'appeler). */
  error: string | null;
}

const IDLE: CallSessionState = {
  call: null,
  phase: null,
  reconnecting: false,
  quality: null,
  micOn: true,
  cameraOn: false,
  speakerOn: false,
  facing: 'user',
  connectedAt: null,
  endReason: null,
  error: null,
};

interface CallStore extends CallSessionState {
  patch: (changes: Partial<CallSessionState>) => void;
  reset: () => void;
}

/**
 * L'appel en cours, un seul à la fois. Le moteur (`shared/services/calls`)
 * l'écrit ; l'écran d'appel et le bandeau « appel en cours » le lisent. La
 * pièce LiveKit, elle, n'y est pas : elle n'a rien d'un état sérialisable.
 */
export const useCallStore = create<CallStore>((set) => ({
  ...IDLE,
  patch: (changes) => set(changes),
  reset: () => set(IDLE),
}));

/** Un appel occupe l'appareil : on ne lance ni second appel ni enregistrement vocal. */
export function isCallBusy(state: CallSessionState): boolean {
  return state.phase !== null && state.phase !== 'ended';
}
